import { useState, useRef, useEffect } from 'react'
import {
  Settings, Download, Upload, Trash2, RotateCcw, Tag, AlertTriangle,
  ChevronLeft, ChevronRight, Sun, Moon,
} from 'lucide-react'
import { useTheme } from '@/hooks/useTheme'
import { APP_VERSION } from '@/lib/changelog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  downloadExport,
  deleteAllData,
  importData,
  validateImportPayload,
  type ExportPayload,
} from '@/api/data'
import { useArchivedBuckets, useBuckets, useRestoreBucket, useDeleteBucket } from '@/hooks/useBuckets'
import { useArchivedPriorities, useRestorePriority, useDeletePriority } from '@/hooks/usePriorities'
import { useValueTagsWithCounts, useDeleteValueTag, useRenameValueTag } from '@/hooks/useValueTags'
import { useAppSettings, useUpdateSettings } from '@/hooks/useAppSettings'

const ARCHIVE_WINDOW_DAYS = 30

function daysArchived(archivedAt: string): number {
  return Math.floor((Date.now() - new Date(archivedAt).getTime()) / 86_400_000)
}

function ArchiveSection() {
  const archivedBuckets = useArchivedBuckets()
  const archivedPriorities = useArchivedPriorities()
  const activeBuckets = useBuckets()
  const [pendingPermanentId, setPendingPermanentId] = useState<{ id: string; title: string; kind: 'priority' | 'bucket' } | null>(null)
  const restoreBucket = useRestoreBucket()
  const deleteBucket = useDeleteBucket()
  const restorePriority = useRestorePriority()
  const deletePriority = useDeletePriority()

  function bucketName(id?: string): string {
    if (!id) return 'Unsorted'
    return activeBuckets?.find((b) => b.id === id)?.name ?? 'Archived area'
  }

  async function confirmPermanentDelete() {
    if (!pendingPermanentId) return
    if (pendingPermanentId.kind === 'priority') {
      await deletePriority.mutateAsync(pendingPermanentId.id)
    } else {
      await deleteBucket.mutateAsync(pendingPermanentId.id)
    }
    setPendingPermanentId(null)
  }

  const hasArchived =
    (archivedBuckets?.length ?? 0) > 0 || (archivedPriorities?.length ?? 0) > 0

  if (!hasArchived) {
    return <p className="text-xs text-muted-foreground italic">Nothing archived.</p>
  }

  const recentBuckets = archivedBuckets?.filter(b => daysArchived(b.archivedAt!) < ARCHIVE_WINDOW_DAYS) ?? []
  const expiredBuckets = archivedBuckets?.filter(b => daysArchived(b.archivedAt!) >= ARCHIVE_WINDOW_DAYS) ?? []
  const recentPriorities = archivedPriorities?.filter(p => daysArchived(p.archivedAt!) < ARCHIVE_WINDOW_DAYS) ?? []
  const expiredPriorities = archivedPriorities?.filter(p => daysArchived(p.archivedAt!) >= ARCHIVE_WINDOW_DAYS) ?? []

  const hasExpired = expiredBuckets.length > 0 || expiredPriorities.length > 0
  const hasRecent = recentBuckets.length > 0 || recentPriorities.length > 0

  return (
    <>
      <div className="space-y-5">
        {/* Pending permanent deletion (≥30 days) */}
        {hasExpired && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={13} className="text-destructive flex-shrink-0" />
              <p className="text-xs font-medium text-destructive">
                Pending permanent deletion
              </p>
            </div>
            <ul className="space-y-2">
              {expiredBuckets.map(b => (
                <li key={b.id} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: b.color }} />
                    <span className="text-sm truncate">{b.name}</span>
                    <span className="text-[10px] text-muted-foreground flex-shrink-0">area</span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" className="h-6 text-xs gap-1"
                      onClick={() => restoreBucket.mutate(b.id)}>
                      <RotateCcw size={10} /> Restore
                    </Button>
                    <Button size="sm" variant="destructive" className="h-6 text-xs gap-1"
                      onClick={() => setPendingPermanentId({ id: b.id, title: b.name, kind: 'bucket' })}>
                      <Trash2 size={10} /> Delete
                    </Button>
                  </div>
                </li>
              ))}
              {expiredPriorities.map(p => (
                <li key={p.id} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm truncate">{p.title}</p>
                    <p className="text-[10px] text-muted-foreground">{bucketName(p.bucketId)}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" className="h-6 text-xs gap-1"
                      onClick={() => restorePriority.mutate(p.id)}>
                      <RotateCcw size={10} /> Restore
                    </Button>
                    <Button size="sm" variant="destructive" className="h-6 text-xs gap-1"
                      onClick={() => setPendingPermanentId({ id: p.id, title: p.title, kind: 'priority' })}>
                      <Trash2 size={10} /> Delete
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recently archived (<30 days) */}
        {hasRecent && (
          <div className="space-y-3">
            {recentBuckets.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Areas ({recentBuckets.length})
                </p>
                <ul className="space-y-1.5">
                  {recentBuckets.map(b => {
                    const days = daysArchived(b.archivedAt!)
                    const remaining = ARCHIVE_WINDOW_DAYS - days
                    return (
                      <li key={b.id} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: b.color }} />
                          <span className="text-sm truncate">{b.name}</span>
                          <span className="text-[10px] text-muted-foreground flex-shrink-0">
                            {remaining}d left
                          </span>
                        </div>
                        <Button size="sm" variant="outline" className="h-6 text-xs gap-1 flex-shrink-0"
                          onClick={() => restoreBucket.mutate(b.id)}>
                          <RotateCcw size={10} /> Restore
                        </Button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}

            {recentPriorities.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Priorities ({recentPriorities.length})
                </p>
                <ul className="space-y-1.5">
                  {recentPriorities.map(p => {
                    const days = daysArchived(p.archivedAt!)
                    const remaining = ARCHIVE_WINDOW_DAYS - days
                    return (
                      <li key={p.id} className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm truncate">{p.title}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {bucketName(p.bucketId)} · {remaining}d left
                          </p>
                        </div>
                        <Button size="sm" variant="outline" className="h-6 text-xs gap-1 flex-shrink-0"
                          onClick={() => restorePriority.mutate(p.id)}>
                          <RotateCcw size={10} /> Restore
                        </Button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={pendingPermanentId !== null}
        onOpenChange={(open) => { if (!open) setPendingPermanentId(null) }}
        title={`Permanently delete "${pendingPermanentId?.title}"?`}
        description="This cannot be undone. All history, notes, and links for this item will be erased."
        confirmLabel="Permanently delete"
        destructive
        onConfirm={() => void confirmPermanentDelete()}
      />
    </>
  )
}

function BandwidthSection() {
  const settings = useAppSettings()
  const updateSettings = useUpdateSettings()
  const [amber, setAmber] = useState<string>('')
  const [red, setRed] = useState<string>('')

  // Sync local state once settings load
  useEffect(() => {
    if (settings && amber === '') setAmber(String(settings.bandwidthAmberHours))
    if (settings && red === '')   setRed(String(settings.bandwidthRedHours))
  }, [settings]) // eslint-disable-line react-hooks/exhaustive-deps

  function saveAmber() {
    const n = parseInt(amber, 10)
    if (!isNaN(n) && n > 0) updateSettings.mutate({ bandwidthAmberHours: n })
    else setAmber(String(settings?.bandwidthAmberHours ?? 55))
  }

  function saveRed() {
    const n = parseInt(red, 10)
    if (!isNaN(n) && n > 0) updateSettings.mutate({ bandwidthRedHours: n })
    else setRed(String(settings?.bandwidthRedHours ?? 65))
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground leading-relaxed">
        Total committed hours/week across all your active priorities. Below the amber threshold
        is healthy; above red is overloaded.
      </p>
      <div className="flex items-center gap-x-4 gap-y-2 flex-wrap">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-amber-500 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />
          <span className="text-muted-foreground text-xs w-12">Amber</span>
          <input
            type="number"
            min={1}
            max={168}
            value={amber}
            onChange={(e) => setAmber(e.target.value)}
            onBlur={saveAmber}
            className="w-16 text-sm bg-muted rounded px-2 py-1 border border-input focus:outline-none focus:ring-1 focus:ring-ring text-center"
          />
          <span className="text-xs text-muted-foreground">hrs/wk</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
          <span className="text-muted-foreground text-xs w-12">Red</span>
          <input
            type="number"
            min={1}
            max={168}
            value={red}
            onChange={(e) => setRed(e.target.value)}
            onBlur={saveRed}
            className="w-16 text-sm bg-muted rounded px-2 py-1 border border-input focus:outline-none focus:ring-1 focus:ring-ring text-center"
          />
          <span className="text-xs text-muted-foreground">hrs/wk</span>
        </label>
      </div>
    </div>
  )
}

function TagsSection() {
  const tagsWithCounts = useValueTagsWithCounts()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const renameValueTag = useRenameValueTag()
  const deleteValueTag = useDeleteValueTag()

  function startEdit(id: string, label: string) {
    setEditingId(id)
    setEditLabel(label)
  }

  async function commitEdit(id: string, original: string) {
    const trimmed = editLabel.trim()
    if (trimmed && trimmed !== original) await renameValueTag.mutateAsync({ id, label: trimmed })
    setEditingId(null)
  }

  function handleEditKeyDown(e: React.KeyboardEvent<HTMLInputElement>, id: string, original: string) {
    if (e.key === 'Enter') { e.preventDefault(); void commitEdit(id, original) }
    if (e.key === 'Escape') setEditingId(null)
  }

  const pendingDeleteTag = tagsWithCounts?.find((t) => t.tag.id === pendingDeleteId)

  if (!tagsWithCounts || tagsWithCounts.length === 0) {
    return (
      <p className="text-xs text-muted-foreground italic">
        No tags yet. Create them from a priority's Values section.
      </p>
    )
  }

  return (
    <>
      <ul className="space-y-1">
        {tagsWithCounts.map(({ tag, count }) => (
          <li key={tag.id} className="flex items-center gap-2 group">
            <Tag size={11} className="text-muted-foreground flex-shrink-0" />
            {editingId === tag.id ? (
              <input
                autoFocus
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
                onBlur={() => void commitEdit(tag.id, tag.label)}
                onKeyDown={(e) => handleEditKeyDown(e, tag.id, tag.label)}
                className="flex-1 text-sm bg-transparent border-b border-ring focus:outline-none"
              />
            ) : (
              <button
                type="button"
                onClick={() => startEdit(tag.id, tag.label)}
                className="flex-1 text-sm text-left hover:text-foreground/80 transition-colors"
                title="Click to rename"
              >
                {tag.label}
              </button>
            )}
            <span className="text-xs text-muted-foreground flex-shrink-0 tabular-nums">
              {count}
            </span>
            <button
              type="button"
              onClick={() => setPendingDeleteId(tag.id)}
              className="max-md:opacity-100 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive flex-shrink-0"
              aria-label={`Delete tag ${tag.label}`}
            >
              <Trash2 size={12} />
            </button>
          </li>
        ))}
      </ul>

      <ConfirmDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => { if (!open) setPendingDeleteId(null) }}
        title={`Delete "${pendingDeleteTag?.tag.label}"?`}
        description={
          pendingDeleteTag && pendingDeleteTag.count > 0
            ? `This tag is used on ${pendingDeleteTag.count} ${pendingDeleteTag.count === 1 ? 'priority' : 'priorities'} and will be removed from all of them.`
            : 'This tag will be permanently deleted.'
        }
        confirmLabel="Delete tag"
        destructive
        onConfirm={() => { deleteValueTag.mutate(pendingDeleteId!); setPendingDeleteId(null) }}
      />
    </>
  )
}

export function SettingsSheet() {
  const isMobile = useIsMobile()
  const { theme, toggle: toggleTheme } = useTheme()
  const [open, setOpen] = useState(false)
  const [pane, setPane] = useState<'main' | 'danger'>('main')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  function handleOpenChange(o: boolean) {
    setOpen(o)
    if (!o) setPane('main')
  }

  // Import state
  const importInputRef = useRef<HTMLInputElement>(null)
  const [pendingImport, setPendingImport] = useState<ExportPayload | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)

  async function handleExport() {
    setExporting(true)
    await downloadExport()
    setExporting(false)
  }

  async function handleDeleteAll() {
    await deleteAllData()
    window.location.reload()
  }

  function handleImportClick() {
    setImportError(null)
    importInputRef.current?.click()
  }

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = '' // reset so re-selecting same file works

    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const raw = JSON.parse(ev.target?.result as string) as unknown
        const payload = validateImportPayload(raw)
        setPendingImport(payload)
      } catch (err) {
        setImportError(err instanceof Error ? err.message : 'Could not parse file.')
      }
    }
    reader.onerror = () => setImportError('Failed to read file.')
    reader.readAsText(file)
  }

  async function handleImportConfirm() {
    if (!pendingImport) return
    setImporting(true)
    try {
      await importData(pendingImport)
      window.location.reload()
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Import failed.')
      setImporting(false)
    }
    setPendingImport(null)
  }

  const importSummary = pendingImport
    ? [
        pendingImport.buckets.length &&
          `${pendingImport.buckets.length} area${pendingImport.buckets.length !== 1 ? 's' : ''}`,
        pendingImport.priorities.length &&
          `${pendingImport.priorities.length} priorit${pendingImport.priorities.length !== 1 ? 'ies' : 'y'}`,
        pendingImport.journalEntries.length &&
          `${pendingImport.journalEntries.length} journal entr${pendingImport.journalEntries.length !== 1 ? 'ies' : 'y'}`,
        pendingImport.notes.length &&
          `${pendingImport.notes.length} note${pendingImport.notes.length !== 1 ? 's' : ''}`,
      ]
        .filter(Boolean)
        .join(', ')
    : ''

  const mainBody = (
    <>
            {/* Appearance */}
            <div>
              <p className="text-sm font-medium mb-1">Appearance</p>
              <p className="text-xs text-muted-foreground mb-3">
                Choose how Plumb looks on this device.
              </p>
              <div className="flex gap-2" role="group" aria-label="Theme">
                <Button
                  variant={theme === 'light' ? 'secondary' : 'outline'}
                  size="sm"
                  className={`gap-2 ${theme === 'light' ? 'ring-1 ring-ring' : 'text-muted-foreground'}`}
                  onClick={() => { if (theme !== 'light') toggleTheme() }}
                  aria-pressed={theme === 'light'}
                >
                  <Sun size={14} />
                  Light
                </Button>
                <Button
                  variant={theme === 'dark' ? 'secondary' : 'outline'}
                  size="sm"
                  className={`gap-2 ${theme === 'dark' ? 'ring-1 ring-ring' : 'text-muted-foreground'}`}
                  onClick={() => { if (theme !== 'dark') toggleTheme() }}
                  aria-pressed={theme === 'dark'}
                >
                  <Moon size={14} />
                  Dark
                </Button>
              </div>
            </div>

            <Separator />

            {/* Data: export + import */}
            <div>
              <p className="text-sm font-medium mb-1">Your data</p>
              <p className="text-xs text-muted-foreground mb-3">
                Export a complete JSON snapshot of your areas, priorities, notes, and journal
                entries, and keep a copy somewhere safe. Importing a snapshot replaces
                everything currently in Plumb.
              </p>
              <input
                ref={importInputRef}
                type="file"
                accept=".json,application/json"
                className="sr-only"
                onChange={handleImportFile}
                aria-label="Select import file"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => void handleExport()}
                  disabled={exporting}
                >
                  <Download size={14} />
                  {exporting ? 'Preparing…' : 'Export'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={handleImportClick}
                  disabled={importing}
                >
                  <Upload size={14} />
                  {importing ? 'Importing…' : 'Import'}
                </Button>
              </div>
              {importError && (
                <p className="mt-2 text-xs text-destructive">{importError}</p>
              )}
            </div>

            <Separator />

            {/* Archive */}
            <div>
              <p className="text-sm font-medium mb-1">Archive</p>
              <p className="text-xs text-muted-foreground mb-3">
                Archived areas and priorities can be restored here. They disappear from the
                main view but are not gone.
              </p>
              <ArchiveSection />
            </div>

            <Separator />

            {/* Bandwidth */}
            <div>
              <p className="text-sm font-medium mb-1">Bandwidth thresholds</p>
              <BandwidthSection />
            </div>

            <Separator />

            {/* Tags */}
            <div>
              <p className="text-sm font-medium mb-1">Value tags</p>
              <p className="text-xs text-muted-foreground mb-3">
                Tags you've created to label what a priority expresses. Click a label to rename it.
                The number shows how many priorities carry that tag.
              </p>
              <TagsSection />
            </div>

            <Separator />

            {/* Danger zone — details live one pane deeper */}
            <button
              type="button"
              onClick={() => setPane('danger')}
              className="w-full flex items-center justify-between gap-2 px-2 -mx-2 py-3 rounded-md text-sm text-destructive hover:bg-destructive/5 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Trash2 size={14} />
                Delete everything
              </span>
              <ChevronRight size={15} className="opacity-60" />
            </button>

            {/* Version — mirrors the footer so it's reachable on every view */}
            <p className="font-mono text-[11px] text-muted-foreground/60 text-center pt-1">
              plumb v{APP_VERSION}
            </p>
    </>
  )

  const dangerBody = (
    <>
      <button
        type="button"
        onClick={() => setPane('main')}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors py-2 -mt-2 pr-3 self-start"
      >
        <ChevronLeft size={15} />
        Settings
      </button>

      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={15} className="text-destructive flex-shrink-0" />
          <p className="text-sm font-semibold text-destructive">Delete all data</p>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          This permanently removes <strong className="text-foreground">everything</strong> in
          Plumb: every life area, every priority and its full history, all notes, all journal
          entries, weekly reviews, and coaching letters. The app resets to its initial seeded
          state, as if you were starting for the first time.
        </p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          There is <strong className="text-foreground">no undo</strong>. If there is any chance
          you'll want this data back, export a backup first.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 w-full"
          onClick={() => void handleExport()}
          disabled={exporting}
        >
          <Download size={14} />
          {exporting ? 'Preparing…' : 'Export a backup first'}
        </Button>
        <Button
          variant="destructive"
          size="sm"
          className="gap-2 w-full"
          onClick={() => setDeleteOpen(true)}
        >
          <Trash2 size={14} />
          Delete everything
        </Button>
      </div>
    </>
  )

  const body = pane === 'danger' ? dangerBody : mainBody

  const trigger = (
    <Button
      variant="ghost"
      size="icon"
      className="h-11 w-11 md:h-8 md:w-8 max-md:[&_svg]:size-6 text-muted-foreground hover:text-foreground"
      aria-label="Settings"
      onClick={isMobile ? () => setOpen(true) : undefined}
    >
      <Settings size={16} />
    </Button>
  )

  return (
    <>
      {isMobile ? (
        <>
          {trigger}
          <BottomSheet open={open} onOpenChange={handleOpenChange} title={pane === 'danger' ? 'Delete all data' : 'Settings'}>
            <div className="px-5 pb-8 space-y-6 flex flex-col">{body}</div>
          </BottomSheet>
        </>
      ) : (
        <Sheet open={open} onOpenChange={handleOpenChange}>
          <SheetTrigger asChild>{trigger}</SheetTrigger>
          <SheetContent side="right" className="w-full sm:max-w-sm shadow-2xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{pane === 'danger' ? 'Delete all data' : 'Settings'}</SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-6 flex flex-col">{body}</div>
          </SheetContent>
        </Sheet>
      )}

      {/* Import confirmation */}
      <ConfirmDialog
        open={pendingImport !== null}
        onOpenChange={(open) => { if (!open) setPendingImport(null) }}
        title="Replace all data?"
        description={`This will erase your current data and restore: ${importSummary}. The app will reload. There is no undo.`}
        confirmLabel="Yes, import and replace"
        destructive
        onConfirm={() => void handleImportConfirm()}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete all data?"
        description="This will permanently erase every area, priority, note, and journal entry. The app will reload as a blank slate. There is no undo."
        confirmLabel="Yes, delete everything"
        destructive
        onConfirm={handleDeleteAll}
      />
    </>
  )
}
